import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PayPalPayModal from "../components/PayPalPayModal";
import PozzoTopNav from "../components/PozzoTopNav";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { SubPageNav } from "../components/SubPageNav";
import { QRDialog } from "../components/QRDialog";
import AppBottomBar from "../components/AppBottomBar";
import DeliveryMap from "../components/DeliveryMap";
import IngredientPicker from "../components/IngredientPicker";
import { T, tr } from "../lib/i18n";
import { tk, setCheckoutLang } from "../lib/checkoutI18n";
import { fetchRestaurant } from "../lib/api";
import { TakeawayComingSoon } from "../components/TakeawayComingSoon";
import {
  ShoppingBag, Plus, Minus, Trash2, Clock, MapPin, Home,
  CreditCard, Wallet, Loader2, ArrowLeft, Check, AlertCircle,
  Leaf, Flame, Star, Sparkles, ChefHat, ShieldCheck, X,
} from "lucide-react";
import { ConsentNote } from "../components/legal/LegalBits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().slice(0, 10);
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

/**
 * Public takeaway landing page — menu, cart, checkout.
 * Path: /asporto (hidden from home until admin flips the switch).
 */
export default function TakeawayPage({ appMode = false }) {
  const nav = useNavigate();
  const [menu, setMenu] = useState({ categories: [] });
  const [restaurant, setRestaurant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  setCheckoutLang(lang);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [cart, setCart] = useState({}); // { item_id: {item, qty, notes} }
  // Progressive checkout: menu → cart → fulfilment → payment.
  const [step, setStep] = useState("menu");
  const [delivery, setDelivery] = useState({
    inRange: false, distanceKm: null, lat: null, lon: null,
  });
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    mode: "pickup", slot: "", address: "", notes: "",
    payment_method: "cash",
  });
  const [submitting, setSubmitting] = useState(false);
  const [paypalPay, setPaypalPay] = useState(null); // { orderId, amount } while PayPal modal open
  const [qrOpen, setQrOpen] = useState(false);
  const [launch, setLaunch] = useState(null); // {coming_soon,...}; admin preview with ?preview=1
  const previewBypass = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1" && !!localStorage.getItem("admin_token");

  useEffect(() => { axios.get(`${API}/takeaway/status`).then((r) => setLaunch(r.data)).catch(() => setLaunch({ coming_soon: false })); }, []);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { if (appMode) fetchRestaurant().then(setRestaurant).catch(() => {}); }, [appMode]);

  useEffect(() => {
    axios.get(`${API}/takeaway/menu`)
      .then((r) => setMenu(r.data))
      .catch(() => setMenu({ categories: [] }))
      .finally(() => setLoadingMenu(false));
  }, []);

  useEffect(() => {
    axios.get(`${API}/takeaway/slots`, { params: { date_str: date } })
      .then((r) => {
        setConfig(r.data);
        setSlots(r.data.slots || []);
        setForm((f) => ({ ...f, slot: f.slot && r.data.slots.includes(f.slot) ? f.slot : (r.data.slots[0] || "") }));
      })
      .catch(() => setSlots([]));
  }, [date]);

  const cartLines = useMemo(() => Object.values(cart), [cart]);
  const subtotal = cartLines.reduce((s, l) => {
    const extrasTotal = (l.extras || []).reduce((x, e) => x + Number(e.price || 0), 0);
    return s + (l.item.price + extrasTotal) * l.qty;
  }, 0);
  const deliveryFee = (form.mode === "delivery"
    && config
    && subtotal < config.delivery_free_threshold) ? config.delivery_fee : 0;
  const total = subtotal + deliveryFee;
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);

  // Derived: featured items across categories (for "Consigliati dallo chef" rail)
  const featuredItems = useMemo(() => {
    const out = [];
    for (const c of menu.categories) {
      for (const it of c.items) if (it.is_featured) out.push(it);
    }
    return out;
  }, [menu]);
  // Compute the closest upcoming slot for the hero countdown ("Prossimo ritiro fra Nm").
  const nextSlot = slots[0] || null;
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 60000); return () => clearInterval(t); }, []);
  const minutesToNextSlot = useMemo(() => {
    if (!nextSlot || !date) return null;
    const target = new Date(`${date}T${nextSlot}:00`).getTime();
    const diffMin = Math.round((target - nowTs) / 60000);
    return diffMin > 0 ? diffMin : null;
  }, [nextSlot, date, nowTs]);
  // Kitchen status: open when we have any slot for today
  const kitchenOpen = slots.length > 0;

  const categoryRefs = useRef({});
  const scrollToCategory = (name) => {
    const el = categoryRefs.current[name];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addToCart = (item) => {
    // If item has a customizable ingredient picker, open the modal instead
    // of adding straight away. The modal will call our confirm handler
    // which fills the cart line with the selection.
    const hasPicker = (item.included_ingredients?.length || 0) + (item.extras?.length || 0) > 0;
    if (hasPicker) {
      setPickerItem(item);
      return;
    }
    setCart((c) => ({
      ...c,
      [item.id]: { item, qty: (c[item.id]?.qty || 0) + 1, notes: c[item.id]?.notes || "" },
    }));
  };
  const [pickerItem, setPickerItem] = useState(null);
  const confirmPicker = ({ excluded, extras, qty, notes }) => {
    if (!pickerItem) return;
    // A customized item is stored as a UNIQUE cart line so multiple
    // variants of the same product can coexist. Key = item.id + hash of
    // the selection so identical customisations still stack.
    const sig = JSON.stringify({ excluded: [...(excluded || [])].sort(), extras: [...(extras || [])].map((e) => e.name).sort() });
    const key = `${pickerItem.id}::${sig}`;
    setCart((c) => ({
      ...c,
      [key]: {
        item: pickerItem,
        qty: (c[key]?.qty || 0) + qty,
        notes: notes || c[key]?.notes || "",
        excluded_ingredients: excluded || [],
        extras: extras || [],
        line_key: key,
      },
    }));
    setPickerItem(null);
  };
  const decItem = (id) => setCart((c) => {
    const cur = c[id]; if (!cur) return c;
    if (cur.qty <= 1) { const n = { ...c }; delete n[id]; return n; }
    return { ...c, [id]: { ...cur, qty: cur.qty - 1 } };
  });
  const removeItem = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const canCheckout = cartLines.length > 0;
  const belowMin = form.mode === "delivery" && config && subtotal < config.delivery_min_order && subtotal < (config?.min_order_delivery || 25);

  const submit = async (e) => {
    e.preventDefault();
    if (!canCheckout) return;
    if (form.mode === "delivery" && subtotal < (config?.min_order_delivery || 25)) {
      alert(`${tk("minOrder")}: € ${config?.min_order_delivery || 25}`);
      return;
    }
    if (form.mode === "delivery" && !form.address.trim()) {
      alert(tk("enterAddress")); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        mode: form.mode,
        slot: form.slot,
        date,
        payment_method: form.payment_method,
        address: form.address.trim(),
        notes: form.notes.trim(),
        items: cartLines.map((l) => ({
          item_id: l.item.id, qty: l.qty, notes: l.notes,
          excluded_ingredients: l.excluded_ingredients || [],
          extras: l.extras || [],
        })),
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/takeaway/orders`, payload);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (form.payment_method === "paypal") {
        // Show inline PayPal button — capture happens server-side once approved.
        setPaypalPay({ orderId: data.order_id, amount: data.total });
      } else {
        nav(`/asporto/success?order_id=${data.order_id}&mode=${form.payment_method}`);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || tk("orderError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!launch || loadingMenu) {
    return (
      <div className="min-h-screen bg-[#faf3ee] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }
  if (launch.coming_soon && !previewBypass) return <TakeawayComingSoon status={launch} lang={lang} siteNav={!appMode} />;

  const cartButton = step === "menu" ? (
    <button
      onClick={() => setStep("cart")}
      disabled={cartCount === 0}
      data-testid="takeaway-open-cart"
      className="inline-flex items-center gap-2 rounded-full bg-[#f7ebdf] text-[#2b1410] px-3 py-1.5 text-sm font-semibold disabled:opacity-40"
    >
      <ShoppingBag className="h-4 w-4" />
      {tk("cart")} {cartCount > 0 && <span className="rounded-full bg-[#4a1c17] text-[#f7ebdf] px-1.5 py-0.5 text-[10px]">{cartCount}</span>}
    </button>
  ) : (
    <button
      onClick={() => setStep(step === "cart" ? "menu" : (step === "fulfilment" ? "cart" : "fulfilment"))}
      data-testid="takeaway-back"
      className="inline-flex items-center gap-2 rounded-full bg-[#f7ebdf]/10 text-[#f7ebdf] px-3 py-1.5 text-sm font-semibold hover:bg-[#f7ebdf]/20"
    >
      <ArrowLeft className="h-4 w-4" />
      {tk("back")}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#faf3ee] pb-32" data-testid="takeaway-page">
      {appMode ? (
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar onOpenQR={() => setQrOpen(true)} headerRightSlot={cartButton} />
      ) : (
        <PozzoTopNav rightSlot={cartButton} />
      )}

      {step === "menu" ? (
        <main className="max-w-5xl mx-auto px-4 py-6">
          {appMode && <SubPageNav hideDests lang={lang} />}
          <AsportoHero
            kitchenOpen={kitchenOpen}
            nextSlot={nextSlot}
            minutesToNextSlot={minutesToNextSlot}
            config={config}
          />

          {menu.categories.length === 0 ? (
            <div className="mt-10 text-center text-[#7c5a4e]">
              <AlertCircle className="h-10 w-10 mx-auto mb-2" />
              <p>{tk("menuSoon")}</p>
            </div>
          ) : (
            <>
              {featuredItems.length > 0 && (
                <FeaturedRail items={featuredItems} cart={cart} addToCart={addToCart} decItem={decItem} lang={lang} />
              )}

              <CategoryChips categories={menu.categories.map((c) => c.name)} onPick={scrollToCategory} />

              {menu.categories.map((cat) => (
                <section
                  key={cat.name}
                  className="mt-10 scroll-mt-24"
                  ref={(el) => { categoryRefs.current[cat.name] = el; }}
                >
                  <div className="flex items-end gap-3 mb-4">
                    <h2 className="font-serif text-3xl font-bold text-[#2b1410]">{cat.name}</h2>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#c48b31]/60 to-transparent mb-2" />
                    <span className="text-xs text-[#7c5a4e] mb-1">{cat.items.length} {tk("dishes")}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {cat.items.map((it) => (
                      <ProductCard
                        key={it.id}
                        item={it}
                        qty={cart[it.id]?.qty || 0}
                        onAdd={() => addToCart(it)}
                        onDec={() => decItem(it.id)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </>
          )}
        </main>
      ) : (
        <main className="max-w-2xl mx-auto px-4 py-6">
          <CheckoutStepper current={step} onJump={(s) => setStep(s)} canJumpToPayment={cartLines.length > 0} />
          <form onSubmit={submit} className="space-y-4 mt-4">
            {step === "cart" && (
              <>
                <CartRecap
                  lines={cartLines}
                  onDec={decItem}
                  onAdd={(id) => setCart((c) => ({ ...c, [id]: { ...c[id], qty: c[id].qty + 1 } }))}
                  onRemove={removeItem}
                />
                {cartLines.length === 0 ? (
                  <div className="rounded-xl bg-white border border-[#e8dcc8] p-6 text-center text-[#7c5a4e]">
                    {tk("emptyCart")} <button type="button" onClick={() => setStep("menu")} className="underline text-[#4a1c17]">{tk("backToMenu")}</button>
                  </div>
                ) : (
                  <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-1 text-sm">
                    <Row label={tk("subtotal")} value={eur(subtotal)} bold />
                  </div>
                )}
                <button
                  type="button"
                  disabled={cartLines.length === 0}
                  onClick={() => setStep("fulfilment")}
                  data-testid="takeaway-goto-fulfilment"
                  className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-3 font-semibold disabled:opacity-50"
                >
                  {tk("continueFulfil")}
                </button>
              </>
            )}

            {step === "fulfilment" && (
              <>
                <FulfilmentPicker form={form} setForm={setForm} date={date} setDate={setDate} slots={slots} config={config} subtotal={subtotal} />
                {form.mode === "delivery" && (
                  <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#7c5a4e]">{tk("deliveryAddress")}</div>
                    <DeliveryMap
                      originLat={config?.origin_lat}
                      originLon={config?.origin_lon}
                      radiusKm={config?.delivery_radius_km || 10}
                      address={form.address}
                      onAddressChange={(v) => setForm((f) => ({ ...f, address: v }))}
                      onValidated={setDelivery}
                    />
                  </div>
                )}
                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#7c5a4e] mb-2">{tk("kitchenNotes")}</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    placeholder={tk("notesPh")}
                    className="w-full rounded-xl border border-[#e8dcc8] bg-[#faf3ee]/40 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/30"
                    data-testid="takeaway-notes"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setStep("payment")}
                  disabled={
                    !form.slot
                    || (form.mode === "delivery" && (!delivery.inRange || subtotal < (config?.min_order_delivery || 25)))
                  }
                  data-testid="takeaway-goto-payment"
                  className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-3 font-semibold disabled:opacity-50"
                >
                  {tk("continuePay")}
                </button>
                {form.mode === "delivery" && subtotal < (config?.min_order_delivery || 25) && (
                  <p className="text-center text-xs text-amber-800">{tk("minOrder")}: € {config?.min_order_delivery || 25}</p>
                )}
              </>
            )}

            {step === "payment" && (
              <>
                <CustomerFields form={form} setForm={setForm} />
                <PaymentPicker form={form} setForm={setForm} mode={form.mode} />
                <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-1 text-sm">
                  <Row label={tk("subtotal")} value={eur(subtotal)} />
                  {form.mode === "delivery" && <Row label={deliveryFee > 0 ? tk("delivery") : tk("freeDelivery")} value={eur(deliveryFee)} />}
                  <Row label={tk("total")} value={eur(total)} bold />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !form.name || !form.phone || !form.slot}
                  data-testid="takeaway-submit"
                  className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-3 font-semibold disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> : null}
                  {form.payment_method === "stripe" ? `${tk("pay")} € ${total.toFixed(2)}` : `${tk("confirmOrder")} · € ${total.toFixed(2)}`}
                </button>
                <ConsentNote action="Ordinando" />
              </>
            )}
          </form>
        </main>
      )}
      {paypalPay && (
        <PayPalPayModal
          kind="takeaway"
          orderId={paypalPay.orderId}
          amount={paypalPay.amount}
          onSuccess={() => {
            setPaypalPay(null);
            nav(`/asporto/success?order_id=${paypalPay.orderId}&mode=paypal`);
          }}
          onClose={() => setPaypalPay(null)}
        />
      )}
      {step === "menu" && <AppBottomBar current="asporto" lang={lang} spacer={!appMode} />}
      <QRDialog open={qrOpen} onOpenChange={setQrOpen} lang={lang} restaurant={restaurant} />
      <IngredientPicker
        open={!!pickerItem}
        item={pickerItem || {}}
        cart={cart}
        onClose={() => setPickerItem(null)}
        onConfirm={confirmPicker}
      />
    </div>
  );
}

// ---------- Subcomponents ----------

// A single lightweight pill (kept for reuse elsewhere).
function Pill({ children }) {
  return <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-2.5 py-1">{children}</span>;
}

// Rustic hero banner with kitchen status + countdown to next pickup slot.
function AsportoHero({ kitchenOpen, nextSlot, minutesToNextSlot, config }) {
  const humanCountdown = (m) => {
    if (m == null) return null;
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60), r = m % 60;
    return `${h}h${r > 0 ? ` ${r}m` : ""}`;
  };
  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] shadow-lg">
      {/* Looping food video — replaces the CSS wood texture. Muted, playsinline
          and preloaded metadata-only for a fast first paint. Poster shows on
          slow networks and while the file downloads. */}
      <video
        className="absolute inset-0 h-full w-full object-cover pointer-events-none"
        src="/media/hero-takeaway.mp4"
        poster="/media/hero-takeaway-poster.jpg"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      />
      {/* Burgundy overlay to keep text legible over the video */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "linear-gradient(90deg, rgba(43,20,16,0.85) 0%, rgba(43,20,16,0.65) 55%, rgba(43,20,16,0.45) 100%)" }} />
      {/* Golden grain flecks */}
      <div className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(rgba(196,139,49,0.3) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />

      <div className="relative px-6 py-8 sm:px-10 sm:py-12">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#c48b31]">
          <ChefHat className="h-4 w-4" /> {tk("heroTag")}
        </div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold leading-tight">
          {tk("heroTitle1")}<br /><span className="text-[#c48b31] italic">{tk("heroTitle2")}</span>
        </h1>
        <p className="mt-3 max-w-md text-sm sm:text-base text-[#f7ebdf]/80">
          {tk("heroText")}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {kitchenOpen ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 px-3 py-1.5 text-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> {tk("kitchenOpen")} · 19:00 → 22:00
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-3 py-1.5 text-xs">
              <span className="h-2 w-2 rounded-full bg-red-400" /> {tk("kitchenClosed")}
            </span>
          )}
          {minutesToNextSlot != null && (
            <span className="inline-flex items-center gap-2 rounded-full bg-[#c48b31]/20 border border-[#c48b31]/40 px-3 py-1.5 text-xs">
              <Clock className="h-3.5 w-3.5 text-[#e6c46a]" /> {tk("nextPickupIn")} <strong className="text-[#e6c46a]">{humanCountdown(minutesToNextSlot)}</strong> · {nextSlot}
            </span>
          )}
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs">
            <MapPin className="h-3.5 w-3.5" /> {tk("deliveryInfo")} € {config?.delivery_free_threshold || 50}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs">
            <ShieldCheck className="h-3.5 w-3.5" /> {tk("securePay")}
          </span>
        </div>
      </div>
    </section>
  );
}

// Sticky horizontal chip-nav that jumps to a category section.
function CategoryChips({ categories, onPick }) {
  return (
    <div className="sticky top-14 z-20 -mx-4 px-4 py-2 bg-[#faf3ee]/95 backdrop-blur border-b border-[#e8dcc8] mt-6">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onPick(c)}
            data-testid={`asporto-chip-${c}`}
            className="whitespace-nowrap rounded-full border border-[#c48b31]/50 bg-white hover:bg-[#c48b31]/10 px-4 py-1.5 text-sm font-medium text-[#4a1c17] transition-colors"
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

// Horizontal rail with the chef's picks.
function FeaturedRail({ items, cart, addToCart, decItem, lang }) {
  return (
    <section className="mt-6" data-testid="asporto-featured">
      <div className="flex items-center gap-2 mb-3">
        <ChefHat className="h-5 w-5 text-[#c48b31]" />
        <h2 className="font-serif text-xl font-bold text-[#2b1410]">{tr(T.takeawayChefPicks, lang)}</h2>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-2">
        {items.map((it) => (
          <div key={it.id} className="min-w-[240px] max-w-[240px]">
            <ProductCard item={it} qty={cart[it.id]?.qty || 0} onAdd={() => addToCart(it)} onDec={() => decItem(it.id)} compact />
          </div>
        ))}
      </div>
    </section>
  );
}

// Small colored pill describing a dietary/style badge on a product card.
const BADGE_META = {
  veg:            { label: "Veg",             Icon: Leaf,     cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  spicy:          { label: "spicy",        Icon: Flame,    cls: "bg-red-100 text-red-800 border-red-200" },
  signature:      { label: "Signature",       Icon: Star,     cls: "bg-amber-100 text-amber-800 border-amber-200" },
  new:            { label: "newTag",          Icon: Sparkles, cls: "bg-sky-100 text-sky-800 border-sky-200" },
  "senza-glutine":{ label: "glutenFree",   Icon: ShieldCheck, cls: "bg-lime-100 text-lime-800 border-lime-200" },
};
const K_BADGE = { spicy: 1, newTag: 1, glutenFree: 1 };
function BadgeChip({ tag }) {
  const meta = BADGE_META[tag];
  if (!meta) return null;
  const { label, Icon, cls } = meta;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" /> {K_BADGE[label] ? tk(label) : label}
    </span>
  );
}

// Rich product card with warm rustic feel — photo on top, name, description,
// badges, price and animated add button. `compact` shrinks paddings for rails.
function ProductCard({ item, qty, onAdd, onDec, compact = false }) {
  const badges = Array.isArray(item.badges) ? item.badges : [];
  return (
    <div className="group flex flex-col gap-2" data-testid={`takeaway-item-${item.id}`}>
      {/* Photo — displayed at natural aspect ratio with no container, no
          background, no mask, no rounded corners, no forced crop. Because
          Nano-Banana photos are shot on the same #F7EBDF cream as the
          page, the edges blend into the layout and the subject appears
          to truly float on the page. Badges are absolute-positioned
          relative to the img wrapper so they still stick to the corners. */}
      <div className="relative">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-500 origin-center"
          />
        ) : (
          <div className="w-full aspect-[4/3] flex flex-col items-center justify-center text-[#7c5a4e] rounded-2xl"
               style={{ backgroundImage: "repeating-linear-gradient(45deg, #efe4d1 0 6px, #f7ebdf 6px 12px)" }}>
            <ChefHat className="h-8 w-8 opacity-40" />
            <span className="text-[10px] mt-1 opacity-60">{tk("photoSoon")}</span>
          </div>
        )}
        {badges.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-wrap gap-1">
            {badges.slice(0, 3).map((b) => <BadgeChip key={b} tag={b} />)}
          </div>
        )}
        {item.is_featured && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-[#2b1410]/85 backdrop-blur px-2 py-0.5 text-[10px] font-semibold text-[#e6c46a]">
            <ChefHat className="h-3 w-3" /> {tk("chefPick")}
          </div>
        )}
      </div>

      {/* Description card — separate rounded box so the price/CTA sit in
          their own container, distinct from the free-floating photo. */}
      <div className={`rounded-2xl bg-white border border-[#e8dcc8] shadow-sm group-hover:shadow-md group-hover:border-[#c48b31]/60 transition-all duration-200 flex-1 flex flex-col ${compact ? "p-3" : "p-4"}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className={`font-serif font-bold text-[#2b1410] leading-tight ${compact ? "text-base" : "text-lg"}`}>{item.name}</h3>
          <div className="text-right shrink-0">
            <div className={`font-serif font-bold text-[#c48b31] ${compact ? "text-base" : "text-lg"}`}>{eur(item.price)}</div>
          </div>
        </div>
        {item.description && (
          <p className={`mt-1 text-[#7c5a4e] leading-snug ${compact ? "text-xs line-clamp-2" : "text-sm line-clamp-3"}`}>{item.description}</p>
        )}

        <div className="mt-3 flex items-center justify-end">
          {qty > 0 ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] text-[#f7ebdf] px-1.5 py-1" data-testid={`takeaway-qty-${item.id}`}>
              <button type="button" onClick={onDec} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/10 transition"><Minus className="h-3.5 w-3.5" /></button>
              <span className="text-sm font-bold w-6 text-center">{qty}</span>
              <button type="button" onClick={onAdd} className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/10 transition"><Plus className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onAdd}
              data-testid={`takeaway-add-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] active:scale-95 text-[#f7ebdf] px-4 py-1.5 text-sm font-semibold transition-transform"
            >
              <Plus className="h-4 w-4" /> {tk("add")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CartRecap({ lines, onDec, onAdd, onRemove }) {
  if (lines.length === 0) return null;
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4">
      <h3 className="font-serif text-lg font-bold text-[#2b1410] mb-2">{tk("yourOrder")}</h3>
      <div className="divide-y divide-[#e8dcc8]">
        {lines.map((l) => {
          const key = l.line_key || l.item.id;
          const extrasTotal = (l.extras || []).reduce((s, e) => s + Number(e.price || 0), 0);
          const unit = Number(l.item.price) + extrasTotal;
          return (
            <div key={key} className="py-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2b1410]">{l.item.name}</div>
                <div className="text-[11px] text-[#7c5a4e]">{eur(unit)} × {l.qty}</div>
                {l.excluded_ingredients?.length > 0 && (
                  <div className="text-[11px] text-red-700 mt-0.5"><X className="inline h-3 w-3" /> {tk("without")} {l.excluded_ingredients.join(", ")}</div>
                )}
                {l.extras?.length > 0 && (
                  <div className="text-[11px] text-emerald-800 mt-0.5"><Plus className="inline h-3 w-3" /> {l.extras.map((e) => e.name).join(", ")}</div>
                )}
                {l.notes && <div className="text-[11px] text-[#7c5a4e] italic mt-0.5">{tk("notes")}: {l.notes}</div>}
              </div>
              <div className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-1.5 py-0.5">
                <button type="button" onClick={() => onDec(key)} className="h-5 w-5 grid place-items-center"><Minus className="h-3 w-3" /></button>
                <span className="text-xs w-5 text-center">{l.qty}</span>
                <button type="button" onClick={() => onAdd(key)} className="h-5 w-5 grid place-items-center"><Plus className="h-3 w-3" /></button>
              </div>
              <div className="w-14 text-right text-sm font-semibold">{eur(unit * l.qty)}</div>
              <button type="button" onClick={() => onRemove(key)} className="text-[#7c5a4e] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CustomerFields({ form, setForm }) {
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [prefill, setPrefill] = useState(null); // {name, address}
  const onPhoneBlur = async () => {
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 6) return;
    try {
      const { data } = await axios.get(`${API}/takeaway/customer`, { params: { phone: form.phone } });
      if (!data?.found) return;
      // Only auto-fill EMPTY fields so we never overwrite what the user typed.
      setForm((f) => ({
        ...f,
        name:    f.name    || data.name    || "",
        email:   f.email   || data.email   || "",
        address: f.address || data.address || "",
      }));
      if (data.address) setPrefill({ address: data.address });
    } catch { /* ignore */ }
  };
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 grid sm:grid-cols-2 gap-3">
      <Field label={`${tk("name")} *`}><input required value={form.name} onChange={on("name")} data-testid="takeaway-name" className="input" /></Field>
      <Field label={`${tk("phone")} *`}>
        <input required value={form.phone} onChange={on("phone")} onBlur={onPhoneBlur} data-testid="takeaway-phone" placeholder="+39 333 1234567" className="input" />
      </Field>
      <Field label={tk("emailOpt")}><input type="email" value={form.email} onChange={on("email")} data-testid="takeaway-email" className="input" /></Field>
      {prefill?.address && form.mode === "delivery" && (
        <div className="sm:col-span-2 rounded-lg bg-[#c48b31]/10 border border-[#c48b31]/40 px-3 py-2 text-xs text-[#4a1c17]" data-testid="takeaway-prefill-hint">
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-[#c48b31]" />
          {tk("prefillHint")} · <button type="button" onClick={() => { setForm((f) => ({ ...f, address: "" })); setPrefill(null); }} className="underline">reset</button>
        </div>
      )}
    </div>
  );
}

function FulfilmentPicker({ form, setForm, date, setDate, slots, config, subtotal }) {
  const setMode = (m) => setForm((f) => ({ ...f, mode: m }));
  // Quick-slot chips: 'Prossimo' (next available), 'Fra un'ora', and 'Alle 20:30'
  // (peak-time). We look up the closest available slot for each preset so we
  // never let the user pick an already-full one.
  const quick = React.useMemo(() => {
    if (!slots.length) return [];
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const nowStr = `${hh}:${mm}`;
    const in60 = new Date(now.getTime() + 60 * 60000);
    const in60Str = `${String(in60.getHours()).padStart(2, "0")}:${String(in60.getMinutes()).padStart(2, "0")}`;
    const findClosest = (t) => slots.find((s) => s >= t) || null;
    const picks = [];
    const next = findClosest(nowStr);   if (next)      picks.push({ label: tk("next"),     value: next });
    const later = findClosest(in60Str); if (later && later !== next) picks.push({ label: tk("inAnHour"), value: later });
    const peak = slots.find((s) => s === "20:30") || slots.find((s) => s >= "20:00");
    if (peak && !picks.some(p => p.value === peak)) picks.push({ label: `${tk("at")} ${peak}`, value: peak });
    return picks;
  }, [slots]);
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <ModeBtn active={form.mode === "pickup"} onClick={() => setMode("pickup")} testId="takeaway-mode-pickup" icon={Home} label={tk("pickup")} hint={tk("pickupHint")} />
        <ModeBtn active={form.mode === "delivery"} onClick={() => setMode("delivery")} testId="takeaway-mode-delivery" icon={MapPin} label={tk("delivery")} hint={`10 km · min € ${config?.min_order_delivery || 25}`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={tk("date")}>
          <input type="date" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} className="input" />
        </Field>
        <Field label={`${tk("time")} *`}>
          <select required value={form.slot} onChange={(e) => setForm((f) => ({ ...f, slot: e.target.value }))} data-testid="takeaway-slot" className="input">
            {slots.length === 0 && <option value="">{tk("noneAvailable")}</option>}
            {slots.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      {quick.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="takeaway-quick-slots">
          <span className="text-[11px] text-[#7c5a4e] uppercase tracking-wider self-center">{tk("quickSlots")}</span>
          {quick.map((q) => (
            <button
              key={q.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, slot: q.value }))}
              data-testid={`takeaway-quick-${q.value.replace(":","")}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                form.slot === q.value
                  ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]"
                  : "bg-[#faf3ee] text-[#4a1c17] border-[#e8dcc8] hover:bg-[#c48b31]/15"
              }`}
            >
              {q.label} · {q.value}
            </button>
          ))}
        </div>
      )}
      {form.mode === "delivery" && config && subtotal > 0 && subtotal < (config.min_order_delivery || 25) && (
        <div className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> {tk("minOrder")}: € {config.min_order_delivery || 25} — {tk("addMore")} € {((config.min_order_delivery || 25) - subtotal).toFixed(2)}</div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, hint, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-lg border-2 p-3 text-left transition ${active ? "border-[#4a1c17] bg-[#faf3ee]" : "border-[#e8dcc8] bg-white hover:border-[#4a1c17]/40"}`}>
      <Icon className={`h-4 w-4 ${active ? "text-[#4a1c17]" : "text-[#7c5a4e]"}`} />
      <div className="text-sm font-semibold mt-1">{label}</div>
      <div className="text-[11px] text-[#7c5a4e]">{hint}</div>
    </button>
  );
}

function PaymentPicker({ form, setForm, mode }) {
  // POS in struttura is only meaningful for pickup — you can't POS if it's home delivery.
  const canPos = mode === "pickup";
  return (
    <div className="rounded-xl bg-white border border-[#e8dcc8] p-4 grid grid-cols-2 gap-2">
      <ModeBtn active={form.payment_method === "stripe"} onClick={() => setForm((f) => ({ ...f, payment_method: "stripe" }))} testId="takeaway-pay-stripe" icon={CreditCard} label={tk("card")} hint={tk("cardHint")} />
      <ModeBtn active={form.payment_method === "paypal"} onClick={() => setForm((f) => ({ ...f, payment_method: "paypal" }))} testId="takeaway-pay-paypal" icon={Wallet} label="PayPal" hint={tk("paypalHint")} />
      <ModeBtn active={form.payment_method === "cash"} onClick={() => setForm((f) => ({ ...f, payment_method: "cash" }))} testId="takeaway-pay-cash" icon={Wallet} label={tk("cash")} hint={mode === "pickup" ? tk("atPickup") : tk("atDelivery")} />
      {canPos && (
        <ModeBtn active={form.payment_method === "pos"} onClick={() => setForm((f) => ({ ...f, payment_method: "pos" }))} testId="takeaway-pay-pos" icon={CreditCard} label="POS" hint={tk("atPickup")} />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-bold text-[#2b1410] text-base pt-1 border-t border-[#e8dcc8]" : "text-[#7c5a4e]"}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

// Progressive checkout stepper: 1) Carrello  2) Consegna  3) Pagamento.
function CheckoutStepper({ current, onJump, canJumpToPayment }) {
  const steps = [
    { key: "cart",       label: tk("cart") },
    { key: "fulfilment", label: tk("delivery") },
    { key: "payment",    label: tk("payment") },
  ];
  const idx = Math.max(0, steps.findIndex((s) => s.key === current));
  return (
    <ol className="flex items-center gap-2" data-testid="checkout-stepper">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        const enabled = i <= idx || (s.key === "payment" && canJumpToPayment);
        return (
          <React.Fragment key={s.key}>
            <li className="flex-1">
              <button
                type="button"
                disabled={!enabled}
                onClick={() => enabled && onJump(s.key)}
                data-testid={`checkout-step-${s.key}`}
                className={`w-full rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-2 ${
                  active
                    ? "bg-[#4a1c17] text-[#f7ebdf] shadow"
                    : done
                    ? "bg-[#c48b31]/20 text-[#4a1c17] border border-[#c48b31]/40"
                    : "bg-white text-[#7c5a4e] border border-[#e8dcc8]"
                } ${enabled ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
              >
                <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] ${
                  active ? "bg-[#f7ebdf] text-[#4a1c17]" : done ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-[#e8dcc8] text-[#7c5a4e]"
                }`}>
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            </li>
            {i < steps.length - 1 && (
              <li className={`h-px w-4 sm:w-8 ${i < idx ? "bg-[#c48b31]" : "bg-[#e8dcc8]"}`} aria-hidden />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// Small utility injected into the page so we don't need Tailwind class shorthand.
const _style = `
  .input { display:block; width:100%; padding:.5rem .75rem; border-radius:.375rem; border:1px solid #e8dcc8; background:white; font-size:.875rem; outline:none; }
  .input:focus { box-shadow:0 0 0 2px rgba(74,28,23,.25); border-color:#4a1c17; }
`;
if (typeof document !== "undefined" && !document.getElementById("takeaway-style")) {
  const s = document.createElement("style"); s.id = "takeaway-style"; s.textContent = _style; document.head.appendChild(s);
}
